import { AnalysisType, EditNames } from 'config/analysisConfig';
import { PanelComponentKeyType } from 'config/panelConfig';
import type { ToolId } from 'config/toolDefinitionsConfig';
import { configStore } from 'config/configStore';
import {
  clearConnect2DSelection,
  deactivateExclusiveTools,
  deleteActiveAnnotation,
  HistoryRedo,
  HistoryUndo,
  popConnect2DSelection,
  resetAnother,
} from '@features/analysis/rendering/Panels/Renderers/CornerstoneManager';
import {
  ContextPool,
  labelorlineonoff,
  setDirection3DModeling,
  update2DLabel,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import {
  undoCprBoundaryEdit,
  redoCprBoundaryEdit,
} from 'utils/CPRUtilities/cprBoundaryEditHistory';
import {
  undoStroke,
  redoStroke,
} from '@features/analysis/segmentManager';
import { setManualMode } from '@features/analysis/rendering/Panels/manual';
import { handleBrushSize, handleEditModeAction } from '@features/view/Modification';
import { segmentManagerStore } from '@features/analysis/analysisList/components/segmentManagerStore';

type ToolActivator = (toolId: ToolId) => void;

const EDIT_MANUAL_BRUSH_SIZE_DELTA_EVENT =
  'analysis:edit-manual-brush-size-delta';
let closeAlertsByEscape: (() => void) | null = null;

export function setCloseAlertsByEscape(closeAlerts: (() => void) | null) {
  closeAlertsByEscape = closeAlerts;
}

function dispatchManualBrushSizeDelta(delta: number) {
  window.dispatchEvent(
    new CustomEvent(EDIT_MANUAL_BRUSH_SIZE_DELTA_EVENT, {
      detail: { delta },
    }),
  );
}

const is2DModificationPanel = (componentKey?: PanelComponentKeyType) =>
  componentKey === 'Axial' ||
  componentKey === 'Coronal' ||
  componentKey === 'Sagittal';

function isEditMode() {
  return configStore.get().EditMode !== null;
}

function isCprPanel(selectedPanelComponentKey?: PanelComponentKeyType) {
  return selectedPanelComponentKey === 'CPR';
}


function clampBrushSize(size: number, max: number) {
  return Math.max(1, Math.min(max, Math.round(size || 1)));
}

function setCprPixelBrushMode(mode: 'draw' | 'erase') {
  configStore.setData({
    cprModificationTool: 'brush',
    cprPixelBrushMode: mode,
  });
  setManualMode(true);
}

function adjustCprBrushSize(delta: number) {
  const { cprModificationTool, cprPixelBrushSize, cprSculptSize } =
    configStore.get();

  const isSculpt = cprModificationTool === 'sculpt';

  const key = isSculpt ? 'cprSculptSize' : 'cprPixelBrushSize';
  const max = isSculpt ? 20 : 30;

  const value = isSculpt ? cprSculptSize : cprPixelBrushSize;

  const current = clampBrushSize(Number(value) || 5, max);
  const next = clampBrushSize(current + delta, max);

  if (next !== current) {
    configStore.setData({ [key]: next });
  }
}

export function handlePointConnect(analysisType:AnalysisType, selectedPanelComponentKey: PanelComponentKeyType) {
  if(!is2DModificationPanel(selectedPanelComponentKey)) return;
  if (analysisType === 'FFR' || configStore.get().EditMode) {
    segmentManagerStore.connect.toggle?.('point');
  }
}


export function handleKeyF(
  analysisType: AnalysisType,
  selectedPanelComponentKey: PanelComponentKeyType,
) {
  if (!is2DModificationPanel(selectedPanelComponentKey)) return;
  if (analysisType === 'FFR' || configStore.get().EditMode) {
    segmentManagerStore.connect.toggle?.('connect2D');
  }
}

export function handleEscape(selectedPanelComponentKey: PanelComponentKeyType) {
  if (!isEditMode() && closeAlertsByEscape) {
    closeAlertsByEscape();
    return true;
  }

  deactivateExclusiveTools({
    viewportId: selectedPanelComponentKey,
  });

  if(selectedPanelComponentKey === 'Surface3D'){
    handleEditModeAction('Eraser', selectedPanelComponentKey);
  }
  segmentManagerStore.connect.clear?.();
}

export function handleKeyEnter(
  selectedPanelComponentKey: PanelComponentKeyType,
) {
  if (selectedPanelComponentKey === 'Surface3D') {
    handleEditModeAction('commit', selectedPanelComponentKey);
  }else if(is2DModificationPanel(selectedPanelComponentKey)){
    segmentManagerStore.connect.apply?.();
  }
}


export function handleDelete() {
  deleteActiveAnnotation();
}

const isCprPixelBrushModification = (componentKey?: PanelComponentKeyType) =>
  componentKey === 'CPR' && configStore.get().cprModificationTool === 'brush';

export function handleUndo(selectedPanelComponentKey?: PanelComponentKeyType) {

  popConnect2DSelection();
  if (isCprPixelBrushModification(selectedPanelComponentKey)) {
    undoStroke();
    return;
  }
  if (selectedPanelComponentKey === 'CPR' && undoCprBoundaryEdit()) return;
  if (is2DModificationPanel(selectedPanelComponentKey) || selectedPanelComponentKey === 'Surface3D') {
    undoStroke();
    return;
  }
  HistoryUndo();
}

export function handleRedo(selectedPanelComponentKey?: PanelComponentKeyType) {
  if (isCprPixelBrushModification(selectedPanelComponentKey)) {
    redoStroke();
    return;
  }
  if (selectedPanelComponentKey === 'CPR' && redoCprBoundaryEdit()) return;
  if (is2DModificationPanel(selectedPanelComponentKey) || selectedPanelComponentKey === 'Surface3D') {
    redoStroke();
    return;
  }
  HistoryRedo();
}

export function handleReset(analysisType: AnalysisType) {
  console.log('reset type:', analysisType);
  resetAnother(analysisType);
}

export function handle2DLabel(
  analysisType: AnalysisType,
  context: ContextPool,
) {
  update2DLabel(context);
}

export function handleEditCoron(
  selectedPanelComponentKey?: PanelComponentKeyType,
){
  handleEditModeAction('coron', selectedPanelComponentKey);
}

export function handleEditPointConnectShortcut(
  selectedPanelComponentKey?: PanelComponentKeyType,
  handleToolActivate?: ToolActivator,
) {
  if (!isEditMode()) return false;
}

export function handleEditEraser3DShortcut(
  mode: 'none' | 'eraser',
  selectedPanelComponentKey?: PanelComponentKeyType,
) {
  if (selectedPanelComponentKey !== 'Surface3D') return;
  configStore.setData({
    manual3DTool: {
      ...configStore.get().manual3DTool,
      eraser: mode === 'eraser',
    },
  });
}

export function handleEdit3DShortcut(
  mode: 'dilate'| 'erode',
  selectedPanelComponentKey?: PanelComponentKeyType,
){
  handleEditModeAction(mode, selectedPanelComponentKey);
}

export function handleEditBrushSizeShortcut({
  selectedPanelComponentKey,
  activeToolId,
  delta,
}: {
  selectedPanelComponentKey?: PanelComponentKeyType;
  activeToolId?: ToolId;
  delta: number;
}) {
  if (!isEditMode()) return false;

  const { cprModificationTool, EditMode, regulatorConnectThickness } =
    configStore.get();

  const isCprBrush =
    EditMode === EditNames.lumen &&
    ['sculpt', 'brush'].includes(cprModificationTool) &&
    isCprPanel(selectedPanelComponentKey);

  if (isCprBrush) {
    adjustCprBrushSize(delta);
    return true;
  }

  if (!activeToolId) return false;

  if (activeToolId === 'connectPoint') {
    const max = 20;
    const current = clampBrushSize(Number(regulatorConnectThickness) || 5, max);
    const next = clampBrushSize(current + delta, max);
    if (next !== current) {
      configStore.setData({ regulatorConnectThickness: next });
    }
    return true;
  }

  if (!['Brush', 'Eraser', 'Connect2D'].includes(activeToolId)) {
    return false;
  }

  handleBrushSize(delta);
  dispatchManualBrushSizeDelta(delta);

  return true;
}

export function handleToolShortcut({
  toolId,
  selectedPanelComponentKey,
  handleToolActivate,
}: {
  toolId: ToolId;
  selectedPanelComponentKey?: PanelComponentKeyType;
  handleToolActivate?: ToolActivator;
}) {
  if (!handleToolActivate) return false;
  if (
    selectedPanelComponentKey === 'Surface3D' ||
    (selectedPanelComponentKey === 'VR' && !configStore.get().visibleMIP)
  ){
    return;
  }
  configStore.setData({ cprModificationTool: 'none' });

  handleToolActivate(toolId);
  return true;
}

export function handlePrimaryNavigationToolShortcut({
  selectedPanelComponentKey,
  handleToolActivate,
}: {
  selectedPanelComponentKey?: PanelComponentKeyType;
  handleToolActivate?: ToolActivator;
}) {
  if (!handleToolActivate) return false;
  handleToolActivate(
    selectedPanelComponentKey === 'VR' ||
      selectedPanelComponentKey === 'Surface3D'
      ? 'Rotation'
      : 'Scroll',
  );

  configStore.setData({ cprModificationTool: 'none' });

  return true;
}

export function handleLumenLabel(
  analysisType: AnalysisType,
  context: ContextPool,
) {
  labelorlineonoff(analysisType, context, 'lumen');
}

export function handlePlaqueLabel(
  analysisType: AnalysisType,
  context: ContextPool,
) {
  if (analysisType === 'fai') return;
  labelorlineonoff(analysisType, context, 'plaque');
}

export function handleFaiLabel(
  analysisType: AnalysisType,
  context: ContextPool,
) {
  if (analysisType !== 'fai') return;
  labelorlineonoff(analysisType, context, 'fai');
}

export function handleLumenOutline(
  analysisType: AnalysisType,
  context: ContextPool,
) {
  labelorlineonoff(analysisType, context, 'lumenoutline');
}

export function handlePlaqueOutline(
  analysisType: AnalysisType,
  context: ContextPool,
) {
  if (analysisType === 'fai') return;
  labelorlineonoff(analysisType, context, 'plaqueoutline');
}

export function handleMarkerline(
  analysisType: AnalysisType,
  context: ContextPool,
) {
  labelorlineonoff(analysisType, context, 'markerline');
}

export function handleGuideline(
  analysisType: AnalysisType,
  context: ContextPool,
) {
  labelorlineonoff(analysisType, context, 'guideline');
}

export function handleStenosisline(
  analysisType: AnalysisType,
  context: ContextPool,
) {
  if (analysisType !== 'stenosis') return;
  labelorlineonoff(analysisType, context, 'stenosisline');
}

export function handleIsrline(
  analysisType: AnalysisType,
  context: ContextPool,
) {
  if (analysisType !== 'isr') return;
  labelorlineonoff(analysisType, context, 'isrline');
}

export function handleAnnotation() {}

// export function handleKeyA(
//   context: ContextPool,
//   selectedPanelComponentKey: PanelComponentKeyType,
// ) {
//   if (
//     selectedPanelComponentKey === 'Surface3D' ||
//     selectedPanelComponentKey === 'VR'
//   ) {
//     setDirection3DModeling(
//       context,
//       selectedPanelComponentKey,
//       'A',
//     );
//   }
// }

// export function handleKeyL(
//   context: ContextPool,
//   selectedPanelComponentKey: PanelComponentKeyType,
// ) {
//   if (
//     selectedPanelComponentKey === 'Surface3D' ||
//     selectedPanelComponentKey === 'VR'
//   ) {
//     setDirection3DModeling(
//       context,
//       selectedPanelComponentKey,
//       'L',
//     );
//   }
// }

// export function handleKeyH(
//   context: ContextPool,
//   selectedPanelComponentKey: PanelComponentKeyType,
// ) {
//   if (
//     selectedPanelComponentKey === 'Surface3D' ||
//     selectedPanelComponentKey === 'VR'
//   ) {
//     setDirection3DModeling(
//       context,
//       selectedPanelComponentKey,
//       'H',
//     );
//   }
// }

// export function handleKeyP(
//   context: ContextPool,
//   selectedPanelComponentKey: PanelComponentKeyType,
// ) {
//   if (
//     selectedPanelComponentKey === 'Surface3D' ||
//     selectedPanelComponentKey === 'VR'
//   ) {
//     setDirection3DModeling(
//       context,
//       selectedPanelComponentKey,
//       'P',
//     );
//   }
// }

// export function handleKeyR(
//   context: ContextPool,
//   selectedPanelComponentKey: PanelComponentKeyType,
// ) {
//   if (
//     selectedPanelComponentKey === 'Surface3D' ||
//     selectedPanelComponentKey === 'VR'
//   ) {
//     setDirection3DModeling(
//       context,
//       selectedPanelComponentKey,
//       'R',
//     );
//   }
// }

// export function handleKeyF(
//   context: ContextPool,
//   selectedPanelComponentKey: PanelComponentKeyType,
// ) {
//   if (
//     selectedPanelComponentKey === 'Surface3D' ||
//     selectedPanelComponentKey === 'VR'
//   ) {
//     setDirection3DModeling(context, selectedPanelComponentKey, 'F');
//   }
// }

export function handleEditToolShortcut(
  tool:  'Brush' | 'Eraser',
  selectedPanelComponentKey?: PanelComponentKeyType,
  handleToolActivate?: ToolActivator,
) {
  if (!isEditMode()) return false;

  if (selectedPanelComponentKey === 'Surface3D') {
    if (tool === 'Eraser') {
      handleEdit3DShortcut('erode', selectedPanelComponentKey);
      return;
    }
    return;
  }

  if (
    configStore.get().EditMode === EditNames.lumen &&
    isCprPanel(selectedPanelComponentKey)
  ) {
    setCprPixelBrushMode(tool === 'Eraser' ? 'erase' : 'draw');
    return;
  }

  segmentManagerStore.connect.clear?.();
  handleToolActivate?.(tool);
  setManualMode(true);

  if (tool === 'Eraser') {
    if (is2DModificationPanel(selectedPanelComponentKey)) {
      clearConnect2DSelection();
    }

    if (selectedPanelComponentKey) {
      handleEditModeAction('Eraser', selectedPanelComponentKey);
    }
  }
  return;
}
